import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DealOverview } from "./deal-detail/DealOverview";
import { DealActivities } from "./deal-detail/DealActivities";
import { DealTasks } from "./deal-detail/DealTasks";
import { DealFiles } from "./deal-detail/DealFiles";
import { DealHistory } from "./deal-detail/DealHistory";

interface DealDetailViewProps {
  dealId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function DealDetailView({
  dealId,
  open,
  onOpenChange,
  onSuccess,
}: DealDetailViewProps) {
  const [deal, setDeal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (dealId && open) {
      fetchDealDetails();
    }
  }, [dealId, open]);

  const fetchDealDetails = async () => {
    if (!dealId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("deals")
        .select(`
          *,
          contact:contacts(*),
          pipeline_stage:pipeline_stages(*)
        `)
        .eq("id", dealId)
        .single();

      if (error) throw error;
      setDeal(data);
    } catch (error) {
      console.error("Error fetching deal details:", error);
      toast({
        title: "Error",
        description: "Failed to load deal details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !deal) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <div className="flex items-center justify-center p-8">
            Loading deal details...
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl">{deal.title}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="activities">Activities</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto">
            <TabsContent value="overview" className="mt-4">
              <DealOverview deal={deal} onUpdate={fetchDealDetails} onSuccess={onSuccess} />
            </TabsContent>

            <TabsContent value="activities" className="mt-4">
              <DealActivities dealId={deal.id} contactId={deal.contact_id} />
            </TabsContent>

            <TabsContent value="tasks" className="mt-4">
              <DealTasks dealId={deal.id} />
            </TabsContent>

            <TabsContent value="files" className="mt-4">
              <DealFiles dealId={deal.id} />
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <DealHistory dealId={deal.id} />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
